/**
 * Unified Image Handler for WhatsApp Flow Builder
 * Encapsulates all image processing logic and format conversions
 */

import { DEFAULT_PLACEHOLDER_IMAGE } from '../flow-core/defaultImage';

class ImageHandler {
  // Constants for image format detection
  static FORMATS = {
    JPEG_HEADER: '/9j/',
    PNG_HEADER: 'iVBOR',
    DATA_URI_PREFIX: 'data:',
    MAX_SIZE: 307200, // 300KB, WhatsApp's own cap on an image inside a flow
    /**
     * What the browser will hand to the server, which resizes anything over MAX_SIZE down to
     * fit. Refusing a 2MB photograph here would be refusing the picture people actually have,
     * to save a round trip. The ceiling only stops someone uploading a RAW file by mistake.
     */
    MAX_UPLOAD_SIZE: 10 * 1024 * 1024,
  };

  // Per-session cache of URL -> base64 conversion promises (see convertURLToBase64).
  static _urlToBase64Cache = new Map();

  /**
   * Check if a string is a raw base64 image
   */
  static isRawBase64(src) {
    return src && (src.startsWith(ImageHandler.FORMATS.JPEG_HEADER) || src.startsWith(ImageHandler.FORMATS.PNG_HEADER));
  }

  /**
   * Check if a string is a data URI
   */
  static isDataURI(src) {
    return src && src.startsWith(ImageHandler.FORMATS.DATA_URI_PREFIX);
  }

  /**
   * Check if a string is a URL
   */
  static isURL(src) {
    return src && (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/'));
  }

  /**
   * Convert any image source to raw base64 (WhatsApp format)
   */
  static async toWhatsAppFormat(src) {
    if (!src) return ImageHandler.getDefaultImage();

    // Already raw base64
    if (ImageHandler.isRawBase64(src)) {
      return src;
    }

    // Data URI - strip header
    if (ImageHandler.isDataURI(src)) {
      return ImageHandler.stripDataURIHeader(src);
    }

    // URL - convert to base64 and strip header
    if (ImageHandler.isURL(src)) {
      try {
        const dataURI = await ImageHandler.convertURLToBase64(src);
        return ImageHandler.stripDataURIHeader(dataURI);
      } catch (error) {
        console.warn('Failed to convert URL to base64:', error.message);
        return ImageHandler.getDefaultImage();
      }
    }

    // Fallback
    return ImageHandler.getDefaultImage();
  }

  /**
   * Convert any image source to display format (data URI for browsers)
   */
  static toDisplayFormat(src, mimeType = 'image/jpeg') {
    if (!src) return '';

    // Already a data URI
    if (ImageHandler.isDataURI(src)) {
      return src;
    }

    // Raw base64 - add header
    if (ImageHandler.isRawBase64(src)) {
      return `data:${mimeType};base64,${src}`;
    }

    // URL - return as is for now
    if (ImageHandler.isURL(src)) {
      return src;
    }

    return '';
  }

  /**
   * Convert a file to raw base64 format
   */
  static async convertFile(file) {
    // Validate file
    ImageHandler.validateFile(file);

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const dataURI = event.target.result;
        const rawBase64 = ImageHandler.stripDataURIHeader(dataURI);
        resolve(rawBase64);
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Validate file size and type
   */
  static validateFile(file) {
    if (file.size > ImageHandler.FORMATS.MAX_UPLOAD_SIZE) {
      throw new Error(`File too large: ${ImageHandler.formatFileSize(file.size)} exceeds 10MB`);
    }
    
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type.toLowerCase())) {
      throw new Error(`Invalid file type: ${file.type}. Only JPEG and PNG are supported.`);
    }
  }

  /**
   * Get default placeholder image (raw base64)
   */
  static getDefaultImage() {
    return DEFAULT_PLACEHOLDER_IMAGE;
  }

  /**
   * Get image format description for UI
   */
  static getFormatDescription(src) {
    if (!src) return 'No image';
    
    if (ImageHandler.isRawBase64(src) || ImageHandler.isDataURI(src)) {
      return 'Uploaded image';
    }
    
    if (ImageHandler.isURL(src)) {
      return 'Image from URL';
    }
    
    return 'Unknown format';
  }

  // Private helper methods
  static stripDataURIHeader(dataURI) {
    const commaIndex = dataURI.indexOf(',');
    return commaIndex === -1 ? dataURI : dataURI.substring(commaIndex + 1);
  }

  static async convertURLToBase64(url) {
    // A URL converts to the same base64 deterministically, but it is re-fetched + re-encoded on
    // every save / preview / export. Cache the (in-flight) promise per URL so each image is only
    // loaded and canvas-encoded once per session. On failure, evict so a later attempt can retry.
    const cached = ImageHandler._urlToBase64Cache.get(url);
    if (cached) {
      return cached;
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx.drawImage(img, 0, 0);

          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          resolve(dataUrl);
        } catch (error) {
          reject(new Error(`Failed to convert image: ${error.message}`));
        }
      };

      img.onerror = () => reject(new Error(`Failed to load image from URL: ${url}`));

      if (url.startsWith('/')) {
        img.src = `${window.location.origin}${url}`;
      } else {
        img.src = url;
      }
    });

    promise.catch(() => ImageHandler._urlToBase64Cache.delete(url));
    ImageHandler._urlToBase64Cache.set(url, promise);
    return promise;
  }

  static formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Process entire flow JSON to ensure all images are WhatsApp compatible
   */
  static async processFlowForWhatsApp(flowJson) {
    const processedFlow = JSON.parse(JSON.stringify(flowJson));
    
    for (const screen of processedFlow.screens) {
      if (screen.layout && screen.layout.children) {
        for (const component of screen.layout.children) {
          // Handle Image components
          if (component.type === 'Image' && component.src) {
            component.src = await ImageHandler.toWhatsAppFormat(component.src);
          }
          
          // Handle ImageCarousel components
          if (component.type === 'ImageCarousel' && component.images) {
            for (const image of component.images) {
              if (image.src) {
                image.src = await ImageHandler.toWhatsAppFormat(image.src);
              }
            }
          }
        }
      }
    }
    
    return processedFlow;
  }
}

export default ImageHandler;